import {
	findGeneratedStorefrontAppLinks,
	type StorefrontAppLinksLookupModel,
} from '../constants/mobile-app-links';
import { normalizeAppStoreLink } from './app-store-links';

/**
 * PD-16558 — a storefront's `/app` page promotes the brand's mobile app and
 * is where a welcome text or email lands when the storefront has one: the
 * page offers the phone's own store (a QR code on a computer) and a
 * "Continue in browser" button that carries on to the original link.
 *
 * These rules are shared by login, which builds the links, and the
 * storefront, which follows them, so the two can never disagree about which
 * link may be carried through the page.
 */

/** The storefront path of the app page. */
export const APP_PROMOTION_PATH = '/app';

/** The query parameter carrying the link "Continue in browser" opens. */
export const APP_PROMOTION_CONTINUE_PARAM = 'continue';

/**
 * The only storefront pages "Continue in browser" may open: the two a
 * welcome message links. Anything else is dropped, so the page can never be
 * used to send a visitor to another site (an open redirect) or to an
 * arbitrary page with attacker-chosen parameters.
 */
const CONTINUE_PATHS: ReadonlyArray<string> = [
	'/sign-in-link',
	'/set-password',
];

/**
 * Stands in for the storefront's own origin while a relative link is
 * checked; `.invalid` is reserved, so it never names a real host.
 */
const PLACEHOLDER_ORIGIN = 'https://storefront.invalid';

/** A storefront's app store links, each already a valid store link or null. */
export interface StoreAppLinksModel {
	appStoreUrl: string | null;
	googlePlayUrl: string | null;
}

/** The store links from a portal site's settings, validated. */
export function storeAppLinksFromSettings(
	settings:
		| { appStoreUrl?: unknown; googlePlayUrl?: unknown }
		| null
		| undefined,
): StoreAppLinksModel {
	return {
		appStoreUrl: normalizeAppStoreLink('appStore', settings?.appStoreUrl),
		googlePlayUrl: normalizeAppStoreLink('googlePlay', settings?.googlePlayUrl),
	};
}

/**
 * PD-17280 — a storefront's store links: the ones generated from its live
 * app's tenant file when it has one (apps/ecommerce-mobile/tenants, set at
 * go-live by release-app), otherwise the portal site's own settings. The
 * generated links win so a go-live needs no per-site settings edit, and so a
 * stale or mistyped setting cannot point the badges somewhere else; the
 * settings remain for any storefront the tenant files do not describe.
 */
export function storeAppLinksForStorefront(
	lookup: StorefrontAppLinksLookupModel & {
		settings: { appStoreUrl?: unknown; googlePlayUrl?: unknown } | null | undefined;
	},
): StoreAppLinksModel {
	const generated = findGeneratedStorefrontAppLinks(lookup);
	if (generated != null) {
		return {
			appStoreUrl: normalizeAppStoreLink('appStore', generated.appStoreUrl),
			googlePlayUrl: normalizeAppStoreLink('googlePlay', generated.googlePlayUrl),
		};
	}
	return storeAppLinksFromSettings(lookup.settings);
}

/** True when the storefront has an app in at least one store. */
export function hasStoreApp(
	links: Partial<StoreAppLinksModel> | null | undefined,
): boolean {
	return (
		(links?.appStoreUrl != null && links.appStoreUrl !== '') ||
		(links?.googlePlayUrl != null && links.googlePlayUrl !== '')
	);
}

/**
 * The numeric Apple app id in an App Store link
 * ("https://apps.apple.com/us/app/capital-trading/id6788757788" →
 * "6788757788"), or null when the link has none. Derived rather than asked
 * for, so an admin pastes one link and the two can never disagree.
 */
export function appleAppIdFromAppStoreLink(
	link: string | null | undefined,
): string | null {
	const valid = normalizeAppStoreLink('appStore', link);
	if (valid == null) {
		return null;
	}
	const match = /\/id(\d+)(?:[/?#]|$)/.exec(new URL(valid).pathname);
	return match?.[1] ?? null;
}

/**
 * The storefront-relative link "Continue in browser" may open, or null when
 * the value is missing or is not one of the allowed pages on this same site.
 * The query string is kept (it carries the one-time token); a fragment is
 * dropped.
 */
export function resolveAppPromotionContinue(
	value: string | null | undefined,
): string | null {
	// Only a same-site path: '//host' and '\host' are read by browsers as
	// another host, so they are refused before parsing.
	if (
		value == null ||
		!value.startsWith('/') ||
		value.startsWith('//') ||
		value.includes('\\')
	) {
		return null;
	}

	let url: URL;
	try {
		url = new URL(value, PLACEHOLDER_ORIGIN);
	} catch {
		return null;
	}

	if (
		url.origin !== PLACEHOLDER_ORIGIN ||
		!CONTINUE_PATHS.includes(url.pathname)
	) {
		return null;
	}

	return `${url.pathname}${url.search}`;
}

/**
 * Route a storefront welcome link through the storefront's app page:
 * "https://shop.example.com/set-password?token=abc" becomes
 * "https://shop.example.com/app?continue=%2Fset-password%3Ftoken%3Dabc".
 *
 * Returns the link unchanged when it is not an absolute https link to one
 * of the allowed pages, so a caller can apply it without checking first.
 */
export function routeThroughAppPromotion(link: string): string {
	let url: URL;
	try {
		url = new URL(link);
	} catch {
		return link;
	}

	if (url.protocol !== 'https:') {
		return link;
	}

	const continuePath = resolveAppPromotionContinue(
		`${url.pathname}${url.search}`,
	);
	if (continuePath == null) {
		return link;
	}

	const params = new URLSearchParams({
		[APP_PROMOTION_CONTINUE_PARAM]: continuePath,
	});
	return `${url.origin}${APP_PROMOTION_PATH}?${params.toString()}`;
}
